import * as d3 from 'd3';
import * as math from 'mathjs';
import Datum from '../../interfaces/Datum';
import ObservableMigration from './ObservableMigration';
import { linkHorizontal } from 'd3';
import * as hcm from './HCM';

export default class AreaGroupRenderer {
  public cutoffArea = { stroke: 'red', fill: 'rgba( 255 , 0 , 0 , 0.063 )', d: '' };
  public componentArea = { stroke: 'blue', fill: 'rgba( 0 , 0 , 255 , 0.063 )', d: '' };

  private client: ObservableMigration;

  private closedCurveGenerator: d3.Line<[number, number]> = d3
    .line()
    .curve(d3.curveBasisClosed);

  private openCurveGenerator: d3.Line<[number, number]> = d3
    .line()
    .curve(d3.curveBasis);

  constructor(client: ObservableMigration) {
    this.client = client;
  }

  public render(links: d3.Selection<
    SVGPathElement,
    d3.HierarchyPointLink<Datum>,
    SVGGElement,
    any>,
                verticalLayout: 'linearTidy' | 'radialTidy') {

    switch (verticalLayout) {
      case 'linearTidy':
        this.renderLinear(links);
        break;
      case 'radialTidy':
        this.renderRadial(links);
        break;
    }

  }

  private renderLinear(links: d3.Selection<
    SVGPathElement,
    d3.HierarchyPointLink<Datum>,
    SVGGElement,
    any>) {

    this.cutoffArea.fill = 'rgba( 0 , 0 , 0 , 0.000 )';
    this.componentArea.fill = 'rgba( 0 , 0 , 0 , 0.000 )';

    const linksComponentSource = links
      .filter(d => hcm.includes(this.client.components, d.source))
      .data();

    const linksComponentTarget = links
      .filter(d => hcm.includes(this.client.components, d.target))
      .data();

    // Draw Cutoff Area
    const cutoffLinePoints = linksComponentTarget
      .map(link => {
        const points: Array<[number, number]> = [];
        const y = 0.5 * (link.source.y + link.target.y);
        points.push([0.5 * (link.source.x + link.target.x), y]);
        if (link.target.children) {
          points.push([link.target.children[0].x, y]);
          points.push([link.target.children[1].x, y]);
        }
        return points;
      })
      .flat(1)
      .sort((a: [number, number], b: [number, number]) => a[0] - b[0]);
    const cutoffLine = this.openCurveGenerator(cutoffLinePoints);

    const componentLinePoints = linksComponentSource
      .map(link => {
        const points: Array<[number, number]> = [];
        const y = 0.5 * (link.source.y + link.target.y);
        points.push([0.5 * (link.source.x + link.target.x), y]);
        points.push([link.target.x, y]);
        return points;
      })
      .flat(1)
      .sort((a: [number, number], b: [number, number]) => a[0] - b[0]);
    const componentLine = this.openCurveGenerator(componentLinePoints);

    const cutoffAreaSelection = d3.select('path#cutoffArea');
    const componentAreaSelection = d3.select('path#componentArea');

    return Promise.all([
      cutoffAreaSelection
        .attr('opacity', cutoffAreaSelection.attr('opacity'))
        .transition().duration(250)
        .attr('opacity', 0)
        .transition().duration(250)
        .attr('opacity', cutoffAreaSelection.attr('opacity'))
        .on('start', () => this.cutoffArea.d = `${cutoffLine}`),
      componentAreaSelection
        .attr('opacity', componentAreaSelection.attr('opacity'))
        .transition().duration(250)
        .attr('opacity', 0)
        .transition().duration(250)
        .attr('opacity', componentAreaSelection.attr('opacity'))
        .on('start', () => {
          this.componentArea.d = `${componentLine}`;
        }),
    ]);
  }

  private renderRadial(links: d3.Selection<
    SVGPathElement,
    d3.HierarchyPointLink<Datum>,
    SVGGElement,
    any>) {

    this.cutoffArea.fill = 'rgba( 255 , 0 , 0 , 0.063 )';
    this.componentArea.fill = 'rgba( 0 , 0 , 255 , 0.063 )';

    const linksComponentSource = links
      .filter(d => hcm.includes(this.client.components, d.source))
      .data();

    const linksComponentTarget = links
      .filter(d => hcm.includes(this.client.components, d.target))
      .data();

    // Draw Cutoff Area
    const cutoffLine = this.closedCurveGenerator(
      linksComponentTarget
        .map(this.relativeInterpolator(0.5, 0.5))
        .sort(this.comparePoints)
        .map(this.transformPoint)
    );

    // Draw Component Area
    const cutoffLineOffset = this.closedCurveGenerator(
      linksComponentSource
        .map(this.absoluteInterpolator(-20))
        .sort(this.comparePoints)
        .map(this.transformPoint)
    );
    const componentLine = this.closedCurveGenerator(
      linksComponentSource
        .map(this.absoluteInterpolator(20))
        .sort(this.comparePoints)
        .map(this.transformPoint)
    );

    const cutoffAreaSelection = d3.select('path#cutoffArea');
    const componentAreaSelection = d3.select('path#componentArea');

    return Promise.all([
      cutoffAreaSelection
        .attr('opacity', cutoffAreaSelection.attr('opacity'))
        .transition().duration(250)
        .attr('opacity', 0)
        .transition().duration(250)
        .attr('opacity', cutoffAreaSelection.attr('opacity'))
        .on('start', () => this.cutoffArea.d = `${cutoffLine}`),
      componentAreaSelection
        .attr('opacity', componentAreaSelection.attr('opacity'))
        .transition().duration(250)
        .attr('opacity', 0)
        .transition().duration(250)
        .attr('opacity', componentAreaSelection.attr('opacity'))
        .on('start', () => {
          this.componentArea.d = `${cutoffLineOffset},${componentLine}`;
        })
    ]);
  }

  private relativeInterpolator(xt: number, yt: number) {
    return (l: d3.HierarchyPointLink<Datum>) => {
      return this.interpolatePoints(l.source, l.target, xt, yt);
    };
  }

  private absoluteInterpolator(t: number) {
    return (l: d3.HierarchyPointLink<Datum>) => {
      const sp = [l.source.x, l.source.y];
      const tp = [l.target.x, l.target.y];
      let v = math.subtract(tp, sp);
      v = math.divide(v, math.sqrt(math.dot(v as number[], v as number[])));
      const p = math.add(sp, math.multiply(t, v));
      return [p[0], p[1]] as [number, number];
    };
  }

  private interpolatePoints(
    { x: ax, y: ay }: d3.HierarchyPointNode<Datum>,
    { x: bx, y: by }: d3.HierarchyPointNode<Datum>,
    tx: number,
    ty: number): [number, number] {
    return [ax * tx + bx * (1 - tx), ay * ty + by * (1 - ty)];
  }

  private comparePoints(a: [number, number], b: [number, number]) {
    return a[0] - b[0];
  }

  private transformPoint(p0: [number, number]): [number, number] {
    const [x, y] = p0;
    const { pi, cos, sin, multiply, matrix } = math;
    const t = x - pi / 2;
    const c = cos(t);
    const s = sin(t);
    const R = matrix([
      [c, -s, 0],
      [s, c, 0],
      [0, 0, 1]
    ]);
    const T = matrix([
      [1, 0, y],
      [0, 1, 0],
      [0, 0, 1]
    ]);
    const p1 = math.squeeze(multiply(multiply(R, T), matrix([[0], [0], [1]])));
    return [p1.get([0]), p1.get([1])];
  }
}
